import { useEffect, useMemo, useRef, useState } from 'react'
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Alert, App, Button, Form, Space, Spin, Typography } from 'antd'
import dayjs from 'dayjs'
import { ArrowLeft, Save } from 'lucide-react'
import {
  serviceOrderDraftUpdateSchema,
  type OrderGroup,
  type ServiceOrder,
  type ServiceOrderDraftUpdateInput
} from '@super-mega/server/contracts/order'
import { FormAnchorNav } from '@/components/form/form-anchor-nav.tsx'
import { FormPageContainer } from '@/components/form/form-page-container.tsx'
import { FORM_ERROR_SCROLL_OPTIONS } from '@/const/form.ts'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { refreshMoneyQueries } from '@/features/bill/index.ts'
import type { AttachmentDescriptor } from '@/features/files/index.ts'
import { FORM_SECTIONS, ORDER_STATUS } from '@/features/order/const/service-order-edit-form.ts'
import {
  createOrderEditSubmission,
  enterpriseSubmissionNotice
} from '@/features/order/utils/order-enterprise-selection.ts'
import { EnterpriseSelector } from '@/features/service-enterprise/index.ts'
import { useDebouncedValue } from '@/hooks/use-debounced-value.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage, getFirstSchemaError } from '@/utils/error.ts'
import { mergeNumberOptions, mergeOptions } from '@/utils/select-options.ts'
import { OrderEnterprisePanel } from './order-enterprise-panel.tsx'
import { OrderGroupSelectorModal } from './order-group-selector-modal.tsx'
import type { OrderListSearch } from './order-list-search.ts'
import { OrderNotesEditModal } from './order-notes-edit-modal.tsx'
import { ServiceOrderActions } from './service-order-actions.tsx'
import { ServiceOrderEditItemFields } from './service-order-edit-item-fields.tsx'
import { ServiceOrderEditOwnershipFields } from './service-order-edit-ownership-fields.tsx'
import type {
  BusinessOption,
  NumberOption,
  OrderGroupMode,
  ServiceOrderEditFormValues
} from './service-order-form.types.ts'
import { defaultServiceTerm } from './service-term-fields.tsx'
import { showOrderFormErrors } from './utils/order-form-validation.ts'

function getOrderGroupSummary(order?: ServiceOrder): OrderGroup | undefined {
  if (!order?.orderGroupId || !order.orderGroupName) {
    return undefined
  }
  return {
    createdAt: order.createdAt,
    customerId: order.customerId,
    customerName: order.customerName,
    id: order.orderGroupId,
    name: order.orderGroupName,
    orderCount: 0,
    rentalOrderCount: 0,
    serviceOrderCount: 0,
    updatedAt: order.updatedAt
  }
}

export function ServiceOrderEditForm({
  canSubmit,
  canManage,
  orderId,
  returnSearch
}: {
  canSubmit: boolean
  canManage: boolean
  canReadBills: boolean
  orderId: number
  returnSearch: OrderListSearch
}) {
  const [editingNotes, setEditingNotes] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const trpc = useTRPC()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { message, modal } = App.useApp()
  const [confirming, setConfirming] = useState(false)
  const [enterprisePending, setEnterprisePending] = useState(false)
  const [editSubmission] = useState(() => createOrderEditSubmission<ServiceOrderDraftUpdateInput>())
  const [form] = Form.useForm<ServiceOrderEditFormValues>()
  const [customerSearch, setCustomerSearch] = useState<string>()
  const [salespersonSearch, setSalespersonSearch] = useState<string>()
  const [businessSearch, setBusinessSearch] = useState<string>()
  const [selectedCustomer, setSelectedCustomer] = useState<NumberOption>()
  const [selectedSalesperson, setSelectedSalesperson] = useState<NumberOption>()
  const [selectedBusinesses, setSelectedBusinesses] = useState<Record<number, BusinessOption>>({})
  const [orderGroupOverride, setOrderGroupOverride] = useState<OrderGroup | null | undefined>()
  const [orderGroupSelectorOpen, setOrderGroupSelectorOpen] = useState(false)
  const hydratedKey = useRef<string | undefined>(undefined)
  const customerKeyword = useDebouncedValue(customerSearch)
  const salespersonKeyword = useDebouncedValue(salespersonSearch)
  const businessKeyword = useDebouncedValue(businessSearch)
  const customerId = Form.useWatch('customerId', form)
  const orderDetail = useQuery(trpc.order.service.detail.queryOptions({ serviceOrderId: orderId }))
  const customers = useQuery({
    ...trpc.lookup.customers.queryOptions({ purpose: 'order.service.read', keyword: customerKeyword }),
    staleTime: 60_000
  })
  const salespeople = useQuery({
    ...trpc.lookup.members.queryOptions({ purpose: 'order.service.read', keyword: salespersonKeyword }),
    staleTime: 60_000
  })
  const businesses = useQuery({
    ...trpc.lookup.businesses.queryOptions({
      purpose: 'order.service.read',
      keyword: businessKeyword,
      type: 'valueAdded'
    }),
    staleTime: 60_000
  })
  const updateDraft = useMutation(trpc.order.service.updateDraft.mutationOptions())
  const isBusy = useIsMutating({ mutationKey: trpc.order.service.pathKey() }) > 0 || confirming
  const order = orderDetail.data
  const isDetailLoading = orderDetail.isPending
  const isDetailError = orderDetail.isError
  const isUnavailable = orderDetail.isFetched && !order
  const isReadOnly = !canSubmit || order?.editRestriction !== null
  const isSaving = updateDraft.isPending
  const formDisabled = isBusy || isReadOnly
  const hydrationVersion = order ? dayjs(order.updatedAt).toISOString() : 'loading'
  const hydrationKey = `order-${orderId}:${hydrationVersion}`
  const orderGroupFromOrder = useMemo(() => getOrderGroupSummary(order), [order])
  const selectedOrderGroup = orderGroupOverride === undefined ? orderGroupFromOrder : (orderGroupOverride ?? undefined)

  const initialValues = useMemo<ServiceOrderEditFormValues | undefined>(() => {
    if (!order) {
      return undefined
    }
    return {
      contractSettings: order.contractSettings ?? [],
      subjectId: order.subjectId ?? undefined,
      enterpriseSelection: { mode: 'none' },
      businessId: order.businessId,
      costAmount: order.costAmount === null ? undefined : Number(order.costAmount),
      customerId: order.customerId,
      fileIds: order.attachments.map(({ file }) => file.id),
      note: order.note ?? undefined,
      orderGroupId: order.orderGroupId ?? undefined,
      orderGroupMode: order.orderGroupId ? 'existing' : 'none',
      salespersonId: order.salespersonId ?? undefined,
      term: order.term,
      agreedAmount: order.agreedAmount == null ? undefined : Number(order.agreedAmount)
    }
  }, [order])

  useEffect(() => {
    if (!initialValues || hydratedKey.current === hydrationKey || hasUnsavedChanges) {
      return
    }
    form.setFieldsValue(initialValues)
    hydratedKey.current = hydrationKey
  }, [form, hasUnsavedChanges, hydrationKey, initialValues])

  const customerOptions = mergeNumberOptions(
    selectedCustomer ?? (order ? { label: order.customerName, value: order.customerId } : undefined),
    (customers.data ?? []).map(({ label, value }) => ({ label, value }))
  )
  const salespersonOptions = mergeNumberOptions(
    selectedSalesperson ??
      (order?.salespersonId ? { label: order.salespersonName, value: order.salespersonId } : undefined),
    (salespeople.data ?? []).map(({ label, value }) => ({ label, value }))
  )
  const currentBusiness =
    order?.businessId && order.businessName
      ? {
          label: order.businessName,
          referencePrice: order.referencePrice,
          value: order.businessId,
          defaultServiceMode: order.serviceMode
        }
      : undefined
  const businessOptions = mergeOptions<BusinessOption>(
    currentBusiness ? [currentBusiness] : [],
    Object.values(selectedBusinesses),
    businesses.data ?? []
  )
  const initialAttachmentFiles: AttachmentDescriptor[] = order
    ? order.attachments.map(({ file }) => ({
        fileType: file.fileType,
        id: file.id,
        name: file.originName || file.fileName,
        size: Number(file.fileSize)
      }))
    : []

  const goBack = () => {
    void navigate({ search: returnSearch, to: '/orders/service' })
  }

  const restore = () => {
    editSubmission.clear()
    form.resetFields()
    if (initialValues) {
      form.setFieldsValue(initialValues)
    }
    setHasUnsavedChanges(false)
    setOrderGroupOverride(undefined)
    setOrderGroupSelectorOpen(false)
  }

  const save = async (values: ServiceOrderEditFormValues) => {
    if (!order || isReadOnly || isBusy || enterprisePending) {
      return false
    }
    const { businessId, customerId: submittedCustomerId, salespersonId, agreedAmount } = values
    if (!submittedCustomerId || businessId === undefined || (!values.term && agreedAmount === undefined)) {
      return false
    }
    if (values.orderGroupMode === 'existing' && !values.orderGroupId) {
      showOrderFormErrors(form, [{ path: ['orderGroupId'], message: '请选择已有订单组' }])
      void message.error('请选择已有订单组')
      return false
    }

    try {
      const payload = await editSubmission.prepare({ serviceOrderId: orderId, values }, () => ({
        businessId,
        contractSettings: values.contractSettings,
        costAmount: values.costAmount ?? null,
        customerId: submittedCustomerId,
        fileIds: values.fileIds,
        note: values.note?.trim() || null,
        orderGroupId: values.orderGroupMode === 'none' ? null : (values.orderGroupId ?? null),
        ...((order.customerId === submittedCustomerId && order.subjectId) ||
        !values.enterpriseSelection ||
        values.enterpriseSelection.mode === 'none'
          ? { subjectId: order.customerId === submittedCustomerId ? order.subjectId : null }
          : { enterpriseSelection: values.enterpriseSelection }),
        serviceOrderId: orderId,
        salespersonId,
        term: values.term,
        agreedAmount: values.term ? null : agreedAmount
      }))
      const notice = enterpriseSubmissionNotice([payload.enterpriseSelection])
      if (
        notice &&
        !(await modal.confirm({
          title: '确认保存订单及企业资料',
          content: notice,
          okText: '确认保存',
          cancelText: '继续编辑'
        }))
      ) {
        return false
      }
      const parsed = serviceOrderDraftUpdateSchema.safeParse(payload)
      if (!parsed.success) {
        editSubmission.clear()
        showOrderFormErrors(form, parsed.error.issues)
        void message.error(getFirstSchemaError(parsed.error))
        return false
      }
      const updated = await updateDraft.mutateAsync(parsed.data)
      editSubmission.clear()
      setHasUnsavedChanges(false)
      queryClient.setQueryData(trpc.order.service.detail.queryKey({ serviceOrderId: orderId }), updated)
      void message.success('服务订单已更新')
      await Promise.all([
        refreshMoneyQueries(queryClient, trpc),
        queryClient.invalidateQueries(trpc.order.service.billingPreview.queryFilter({ serviceOrderId: orderId })),
        queryClient.invalidateQueries(trpc.order.subjects.list.queryFilter()),
        queryClient.invalidateQueries(trpc.order.subjects.options.queryFilter()),
        queryClient.invalidateQueries(trpc.order.subjects.detail.queryFilter()),
        ...(notice
          ? [
              queryClient.invalidateQueries(trpc.company.reusable.queryFilter()),
              queryClient.invalidateQueries(trpc.company.search.queryFilter()),
              queryClient.invalidateQueries(trpc.identity.reusable.queryFilter()),
              queryClient.invalidateQueries(trpc.identity.search.queryFilter()),
              queryClient.invalidateQueries(trpc.license.list.queryFilter())
            ]
          : []),
        queryClient.invalidateQueries(trpc.order.groups.list.queryFilter())
      ])
      return true
    } catch (error) {
      void message.error(getErrorMessage(error, '服务订单更新失败'))
      return false
    }
  }

  if (isDetailLoading) {
    return (
      <FormPageContainer breadcrumbRender={false} title="编辑服务订单">
        <div className="flex min-h-72 items-center justify-center">
          <Spin description="正在读取订单资料…" />
        </div>
      </FormPageContainer>
    )
  }

  if (isDetailError) {
    return (
      <FormPageContainer breadcrumbRender={false} title="编辑服务订单">
        <Alert
          action={<Button onClick={() => void orderDetail.refetch()}>重试</Button>}
          showIcon
          title={getErrorMessage(orderDetail.error, '订单详情加载失败')}
          type="error"
        />
      </FormPageContainer>
    )
  }

  if (isUnavailable) {
    return (
      <FormPageContainer breadcrumbRender={false} title="编辑服务订单">
        <Alert action={<Button onClick={goBack}>返回列表</Button>} showIcon title="订单不存在或已被删除" type="error" />
      </FormPageContainer>
    )
  }

  const status = order ? ORDER_STATUS[order.status] : undefined

  return (
    <FormPageContainer
      breadcrumbRender={false}
      content="待办理且仅有自动生成费用草稿的订单可修改；保存会按新配置重建草稿，已有草稿的核费修改需重新确认。正式出账后不可直接修改。"
      title={`${isReadOnly ? '查看' : '编辑'}服务订单 · ${order?.orderNo ?? ''}`}
    >
      {order && editingNotes && (
        <OrderNotesEditModal kind="service" order={order} onClose={() => setEditingNotes(false)} />
      )}
      <div className="flex w-full flex-col gap-5 lg:flex-row lg:items-start">
        <div className="min-w-0 flex-1">
          <Form<ServiceOrderEditFormValues>
            disabled={formDisabled}
            form={form}
            id="service-order-edit-form"
            initialValues={initialValues}
            layout="vertical"
            onValuesChange={() => {
              setHasUnsavedChanges(true)
            }}
            onFinish={() => {
              if (isBusy || enterprisePending) {
                return
              }
              setConfirming(true)
              void save(form.getFieldsValue(true)).finally(() => setConfirming(false))
            }}
            scrollToFirstError={FORM_ERROR_SCROLL_OPTIONS}
          >
            <div className="flex flex-col gap-5">
              <Alert
                showIcon
                title={`当前状态：${status?.text ?? ''}。${
                  !canSubmit
                    ? '当前账号没有订单编辑权限。'
                    : (order?.editRestriction ?? '可修改订单内容，保存不改变办理状态。')
                }${isReadOnly && canManage ? '可通过底部按钮编辑备注与附件。' : ''}`}
                type="info"
              />

              <ServiceOrderEditOwnershipFields
                customerId={customerId}
                customerLoading={customers.isLoading}
                customerOptions={customerOptions}
                disabled={formDisabled}
                onCustomerChange={(value) => {
                  const selected = customerOptions.find((option) => option.value === value)
                  if (selected) {
                    setSelectedCustomer(selected)
                  }
                  form.setFieldsValue({
                    subjectId: undefined,
                    enterpriseSelection: { mode: 'none' },
                    orderGroupId: undefined,
                    orderGroupMode: 'none'
                  })
                  setOrderGroupOverride(null)
                  setOrderGroupSelectorOpen(false)
                }}
                onCustomerSearch={setCustomerSearch}
                onClearOrderGroup={() => {
                  setHasUnsavedChanges(true)
                  form.setFieldsValue({ orderGroupId: undefined, orderGroupMode: 'none' })
                  setOrderGroupOverride(null)
                }}
                onOpenOrderGroupSelector={() => setOrderGroupSelectorOpen(true)}
                onOrderGroupModeChange={(mode: Exclude<OrderGroupMode, 'new'>) => {
                  form.setFieldsValue({ orderGroupId: undefined, orderGroupMode: mode })
                  setOrderGroupOverride(null)
                }}
                onSalespersonChange={(value) => {
                  const selected = salespersonOptions.find((option) => option.value === value)
                  if (selected) {
                    setSelectedSalesperson(selected)
                  }
                }}
                onSalespersonSearch={setSalespersonSearch}
                orderGroup={selectedOrderGroup}
                salespersonLoading={salespeople.isLoading}
                salespersonOptions={salespersonOptions}
              />

              <section
                className="border-border bg-surface scroll-mt-24 rounded-xl border p-5 shadow-xs"
                id="section-service-enterprise"
              >
                <Typography.Title className="mb-3!" level={5}>
                  服务对象及资料
                </Typography.Title>
                {order && ((order.subjectId && customerId === order.customerId) || isReadOnly) ? (
                  <OrderEnterprisePanel
                    subjectId={order.subjectId}
                    subjectName={order.subjectName}
                    currentOrder={{ type: 'service', id: order.id }}
                    canSupplement={canManage && order.status !== 'archived'}
                    disabled={isBusy || hasUnsavedChanges}
                    onPendingChange={setEnterprisePending}
                    onSuccess={() => void orderDetail.refetch()}
                  />
                ) : (
                  <Form.Item className="mt-4" label="补关联服务对象" name="enterpriseSelection">
                    <EnterpriseSelector
                      purpose="order.service.read"
                      key={customerId}
                      customerId={customerId}
                      disabled={formDisabled}
                      onPendingChange={setEnterprisePending}
                    />
                  </Form.Item>
                )}
              </section>

              <ServiceOrderEditItemFields
                businessLoading={businesses.isLoading}
                businessOptions={businessOptions}
                disabled={formDisabled}
                initialAttachmentFiles={initialAttachmentFiles}
                onBusinessChange={(value) => {
                  const selected = businessOptions.find((option) => option.value === value)
                  if (selected) {
                    setSelectedBusinesses((previous) => ({ ...previous, [value]: selected }))
                  }
                  if (selected?.defaultServiceMode === 'term') {
                    const term = defaultServiceTerm()
                    term.rules[0].unitPrice = Number(selected.referencePrice ?? 0)
                    form.setFieldValue('term', term)
                    form.setFieldValue('agreedAmount', undefined)
                  } else {
                    form.setFieldValue('term', null)
                    form.setFieldValue(
                      'agreedAmount',
                      selected?.referencePrice == null ? undefined : Number(selected.referencePrice)
                    )
                  }
                }}
                onBusinessSearch={setBusinessSearch}
              />
            </div>
          </Form>
          <div
            aria-label="服务订单操作"
            role="region"
            className="border-border bg-surface/95 sticky bottom-3 z-10 mt-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 shadow-xs backdrop-blur"
          >
            <Button disabled={isBusy} icon={<ArrowLeft aria-hidden size={16} />} onClick={goBack}>
              返回订单列表
            </Button>
            <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
              {hasUnsavedChanges && <span className="text-content-muted text-sm">请先保存修改，再执行办理操作</span>}
              {order && isReadOnly && canManage && (
                <Authorized permissions={['order.service.update']}>
                  <Button disabled={isBusy || hasUnsavedChanges} onClick={() => setEditingNotes(true)}>
                    编辑备注与附件
                  </Button>
                </Authorized>
              )}
              {order && (
                <ServiceOrderActions
                  order={order}
                  canManage={canManage}
                  canReadBills={false}
                  disabled={isBusy || hasUnsavedChanges}
                />
              )}
              {!isReadOnly || hasUnsavedChanges ? (
                <Space wrap>
                  <Button disabled={isBusy} onClick={restore}>
                    恢复已保存内容
                  </Button>
                  {!isReadOnly && (
                    <Button
                      form="service-order-edit-form"
                      htmlType="submit"
                      disabled={isBusy || enterprisePending}
                      icon={<Save aria-hidden size={16} />}
                      loading={isSaving}
                      type="primary"
                    >
                      保存订单
                    </Button>
                  )}
                </Space>
              ) : null}
            </div>
          </div>
        </div>
        <FormAnchorNav className="hidden lg:block" sections={FORM_SECTIONS} />
      </div>
      <OrderGroupSelectorModal
        customerId={customerId}
        initialSelectedGroup={selectedOrderGroup}
        onCancel={() => setOrderGroupSelectorOpen(false)}
        onSelect={(group) => {
          setHasUnsavedChanges(true)
          form.setFieldsValue({ orderGroupId: group.id, orderGroupMode: 'existing' })
          setOrderGroupOverride(group)
          setOrderGroupSelectorOpen(false)
        }}
        open={orderGroupSelectorOpen}
      />
    </FormPageContainer>
  )
}
